import {
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	ContextMenuLinkItem,
	IDrawerDataListColumn,
	Id,
	PaginatedDrawerAutoDataList,
	isSuccess,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { PermissionName } from "../../../shared/types/permission";
import { deletionRequestService } from "../shared/api/deletionRequestService";
import { IDeletionRequestIndexResource, isDeletionRequestDeletable, isDeletionRequestViewable } from "../shared/types";
import { DeletionRequestCreateDrawer } from "./DeletionRequestDrawer";

export const DeletionRequestList = () => {
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const crudOperations = useLaravelCrudEntity(deletionRequestService, {
		entityName: t("Deletion request"),
		messages: {
			create: t("Deletion request was saved."),
			update: t("Deletion request was saved."),
			delete: t("Deletion request was deleted."),
		},
	});
	const canCreate = useHasPermission<PermissionName>(["deletionRequest.create"]);

	const sendDeletionRequest = useCallback(
		async (id: Id) => {
			const res = await deletionRequestService.sendDeletionRequest(id);

			if (isSuccess(res)) {
				await queryClient.invalidateQueries({ queryKey: deletionRequestService.listKey });
				await queryClient.invalidateQueries({ queryKey: deletionRequestService.showKey(id) });

				showToast({
					type: "success",
					title: t("Deletion request"),
					body: t("Successfully sent deletion request"),
				});
			}
		},
		[queryClient, showToast, t],
	);

	const dataListColumns: IDrawerDataListColumn<IDeletionRequestIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Email"),
				valueKey: "email",
				type: "value",
			},
			{
				heading: t("Status"),
				valueKey: "status",
				type: "value",
				render: ({ status }) => t(status),
			},
			{
				heading: t("Created by"),
				valueKey: "createdBy",
				type: "value",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: (deletionRequest) => (
					<ContextMenu displayShortcutsAs="icon">
						{isDeletionRequestViewable(deletionRequest) && (
							<ContextMenuLinkItem
								to={`${deletionRequest.id}`}
								title={t("View deletion request")}
								label={t("View deletion request")}
								icon="eye"
								shortcut="default"
							/>
						)}
						<ContextMenuItem
							title={t("Send deletion request")}
							label={t("Send deletion request")}
							action={() => sendDeletionRequest(deletionRequest.id)}
							icon="send"
							shortcut="default"
						/>
						{isDeletionRequestDeletable(deletionRequest) && (
							<ContextMenuDeleteItem
								action={() => crudOperations.deleteEntity(deletionRequest.id)}
								title={t("Delete deletion request")}
								label={t("Delete deletion request")}
								heading={t("Delete")}
								labels={{ close: t("Close"), confirm: t("Confirm") }}
							>
								{t("Are you sure you want to delete this deletion request?")}
							</ContextMenuDeleteItem>
						)}
					</ContextMenu>
				),
			},
		],
		[crudOperations, sendDeletionRequest, t],
	);

	return (
		<PaginatedDrawerAutoDataList
			canCreate={canCreate}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(deletionRequestService.listKey, deletionRequestService.index)}
			renderCreateDrawer={(drawerProps) => (
				<DeletionRequestCreateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.createEntity(entityFormState)}
				/>
			)}
			translations={{
				title: t("Deletion requests"),
				empty: t("No deletion request available."),
			}}
			onRowClick={({ id }) => navigate(`${id}`)}
			initialSearch={{
				search: "",
				searchFields: [
					{ label: t("Email"), name: "email" },
					{ label: t("Created by"), name: "createdBy" },
				],
			}}
		/>
	);
};
